import { ROBOT_MODES } from 'utils/constants'
import type { IModeSwitchPending } from 'utils/interfaces'
import { mapCatalogOperationFromServer } from 'utils/selectCatalogOperation'

function mapModeSwitchPendingFromWs(
  raw: { mode?: number; requested_at?: string; timeout_sec?: number } | null | undefined
): IModeSwitchPending | null {
  if (!raw || raw.mode == null || !raw.requested_at) {
    return null
  }
  return {
    mode: Number(raw.mode) as ROBOT_MODES,
    requestedAt: raw.requested_at,
    timeoutSec: raw.timeout_sec ?? 10,
  }
}

export function mapActiveModeMetadata(raw: unknown) {
  if (raw === null) {
    return null
  }
  if (!raw || typeof raw !== 'object') {
    return undefined
  }
  const data = raw as Record<string, unknown>
  const action_type = Array.isArray(data.action_type)
    ? data.action_type.map(code => Number(code)).filter(Number.isFinite)
    : undefined
  const port = finitePort(data.port)
  return {
    kind: data.kind != null ? Number(data.kind) : undefined,
    action_type,
    station_id: data.station_id != null ? String(data.station_id) : undefined,
    lot_id: data.lot_id != null ? Number(data.lot_id) : undefined,
    lot_model: data.lot_model != null ? String(data.lot_model) : undefined,
    maintenance_point_id: data.maintenance_point_id != null ? Number(data.maintenance_point_id) : undefined,
    ...(typeof data.caster === 'string' ? { caster: data.caster } : {}),
    ...(port != null ? { port } : {}),
    ...(typeof data.user === 'string' ? { user: data.user } : {}),
    ...(typeof data.mountpoint === 'string' ? { mountpoint: data.mountpoint } : {}),
  }
}

function finitePort(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value)
  }
  return null
}

/** Correction source from telemetry or mode-103 feedback. Password is dropped. */
export function mapNtripFromWs(raw: unknown) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const data = raw as Record<string, unknown>
  const status =
    data.status == null || data.status === '' ? undefined : String(data.status)
  const message =
    data.message == null || data.message === '' ? undefined : String(data.message)
  return {
    caster: data.caster == null ? '' : String(data.caster),
    port: finitePort(data.port),
    user: data.user == null ? '' : String(data.user),
    mountpoint: data.mountpoint == null ? '' : String(data.mountpoint),
    ...(status != null ? { status } : {}),
    ...(message != null ? { message } : {}),
  }
}

function hasOwn(data: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(data, key)
}

function buildRobotModeSync(data: Record<string, unknown>) {
  if (
    data.active_mode == null &&
    !hasOwn(data, 'mode_switch_pending') &&
    !hasOwn(data, 'active_mode_metadata') &&
    !hasOwn(data, 'catalog_operation') &&
    !hasOwn(data, 'catalog_service_action_type')
  ) {
    return undefined
  }

  const catalogServiceActionTypeRaw = data.catalog_service_action_type
  const catalogServiceActionType =
    hasOwn(data, 'catalog_service_action_type') && catalogServiceActionTypeRaw != null
      ? Number(catalogServiceActionTypeRaw)
      : hasOwn(data, 'catalog_service_action_type')
      ? null
      : undefined

  const catalogOperation = hasOwn(data, 'catalog_operation')
    ? mapCatalogOperationFromServer(data.catalog_operation)
    : undefined

  return {
    ...(data.active_mode != null && Number.isFinite(Number(data.active_mode))
      ? { activeMode: Number(data.active_mode) as ROBOT_MODES }
      : {}),
    ...(hasOwn(data, 'mode_switch_pending')
      ? { modeSwitchPending: mapModeSwitchPendingFromWs(data.mode_switch_pending as any) }
      : {}),
    ...(hasOwn(data, 'active_mode_metadata')
      ? { activeModeMetadata: mapActiveModeMetadata(data.active_mode_metadata) }
      : {}),
    ...(catalogOperation !== undefined ? { catalogOperation } : {}),
    ...(catalogServiceActionType !== undefined ? { catalogServiceActionType } : {}),
  }
}

function isWsPartialPatch(data: Record<string, unknown>): boolean {
  return !data.header && !data.odometry
}

function mapSystemDiagnosticsFromWs(raw: unknown) {
  if (raw == null) {
    return null
  }
  if (typeof raw !== 'object') {
    return undefined
  }
  const diagnostics = raw as Record<string, unknown>
  return {
    level: diagnostics.level,
    name: diagnostics.name,
    message: diagnostics.message,
  }
}

export const telemetryPatchFromWs = (data: any) => {
  if (!data || typeof data !== 'object') return null

  if (isWsPartialPatch(data)) {
    const patch: Record<string, unknown> = {}
    if (hasOwn(data, 'system_diagnostics')) {
      patch.system_diagnostics = mapSystemDiagnosticsFromWs(data.system_diagnostics)
    }
    const robotModeSync = buildRobotModeSync(data)
    if (robotModeSync) {
      patch.robotModeSync = robotModeSync
    }
    return Object.keys(patch).length ? patch : null
  }

  const rawAvailableMaintenanceModes = Array.isArray(data.available_maintenance_modes)
    ? data.available_maintenance_modes
    : Array.isArray(data.availableMaintenanceModes)
    ? data.availableMaintenanceModes
    : undefined
  const availableMaintenanceModes = rawAvailableMaintenanceModes
    ?.map((availableMode: any) => ({
      mode: Number(availableMode?.mode),
      metadata:
        availableMode?.metadata != null && String(availableMode.metadata).trim()
          ? String(availableMode.metadata)
          : undefined,
    }))
    .filter((availableMode: { mode: number }) => Number.isFinite(availableMode.mode))
  const rawOnboardServicePoints = Array.isArray(data.onboard_service_points)
    ? data.onboard_service_points
    : Array.isArray(data.onboardServicePoints)
    ? data.onboardServicePoints
    : data.onboard_service_points === null
    ? null
    : undefined
  const onboardServicePoints =
    rawOnboardServicePoints === null
      ? null
      : Array.isArray(rawOnboardServicePoints)
      ? rawOnboardServicePoints
          .filter((row: unknown) => row && typeof row === 'object')
          .map((row: any) => ({
            kind: row.kind != null ? Number(row.kind) : undefined,
            station_id: row.station_id != null ? String(row.station_id) : undefined,
            lot_id: row.lot_id != null ? Number(row.lot_id) : undefined,
            lot_model: row.lot_model != null ? String(row.lot_model) : undefined,
            maintenance_point_id: row.maintenance_point_id != null ? Number(row.maintenance_point_id) : undefined,
          }))
      : undefined
  const robotBatteryPower =
    data.robot_battery !== null &&
    data.robot_battery !== undefined &&
    data.robot_battery.power !== undefined &&
    data.robot_battery.power !== null
      ? data.robot_battery.power
      : data.battery_power !== undefined && data.battery_power !== null
      ? data.battery_power
      : undefined

  const robotBattery =
    data.robot_battery !== null && data.robot_battery !== undefined
      ? {
          power: data.robot_battery.power ?? data.battery_power ?? null,
          current: data.robot_battery.current ?? null,
          charging: data.robot_battery.charging ?? null,
        }
      : data.battery_power !== undefined && data.battery_power !== null
      ? {
          power: data.battery_power,
          current: null,
          charging: null,
        }
      : undefined

  const joystickBattery =
    data.joystick_battery !== null && data.joystick_battery !== undefined
      ? {
          power: data.joystick_battery.power ?? null,
          current: data.joystick_battery.current ?? null,
          charging: data.joystick_battery.charging ?? null,
        }
      : undefined

  const patch: Record<string, unknown> = {
    timestamp: data.header?.stamp?.sec,
    facility_id: data.facility_id,
    batteryPower: robotBatteryPower,
    waterTankFilling: data.water_level,
    dirtyWaterTankFilling: data.dirty_water_level,
    temperatureControlUnit: data.temperature_control_unit,
    temperatureChassis: data.temperature_chassis,
    temperatureLidar: data.temperature_lidar,
    temperatureExternal: data.temperature_external,
    trashFullness: data.garbage_level,
    locks: data.locks,
    cleaningEquipmentState: data.cleaning_equipment_state,
    workingType: data.working_type ?? undefined,
    statusCode: data.status_code,
    quaternion: data.odometry?.pose?.pose?.orientation,
    velocity: data.odometry?.twist?.twist?.linear?.x,
  }

  if (data.localization_info != null) {
    patch.localization_info = {
      localization_status: data.localization_info.localization_status ?? false,
      localization_mode: data.localization_info.localization_mode ?? 0,
      imu_bias_status: data.localization_info.imu_bias_status ?? 0,
      gnss_status: data.localization_info.gnss_status ?? 0,
      gnss1_status: data.localization_info.gnss1_status ?? null,
      gnss2_status: data.localization_info.gnss2_status ?? null,
      lidar_maps: data.localization_info.lidar_maps ?? [],
      current_lidar_map: data.localization_info.current_lidar_map ?? '',
    }
  }

  if (data.firmware_info) {
    patch.firmware_info = {
      current_version: data.firmware_info.current_version ?? '',
      latest_version: data.firmware_info.latest_version ?? '',
    }
  }

  if (rawAvailableMaintenanceModes !== undefined) {
    patch.availableMaintenanceModes = availableMaintenanceModes
  }

  if (rawOnboardServicePoints !== undefined) {
    patch.onboardServicePoints = onboardServicePoints
  }

  if (robotBattery !== undefined) {
    patch.robotBattery = robotBattery
  }

  if (joystickBattery !== undefined) {
    patch.joystickBattery = joystickBattery
  }

  if (hasOwn(data, 'system_diagnostics')) {
    patch.system_diagnostics = mapSystemDiagnosticsFromWs(data.system_diagnostics)
  }

  if (data.vcu) {
    patch.vcu = {
      turbine_motor: data.vcu.turbine_motor
        ? {
            speed: data.vcu.turbine_motor.speed ?? null,
            torque: data.vcu.turbine_motor.torque ?? null,
          }
        : undefined,
      scrubber_brush_motor: data.vcu.scrubber_brush_motor
        ? {
            speed: data.vcu.scrubber_brush_motor.speed ?? null,
            torque: data.vcu.scrubber_brush_motor.torque ?? null,
          }
        : undefined,
    }
  }

  if (data.charger !== undefined) {
    patch.charger = data.charger
  }

  if (!isWsPartialPatch(data)) {
    patch.ntrip = hasOwn(data, 'ntrip') ? mapNtripFromWs(data.ntrip) : undefined
  } else if (hasOwn(data, 'ntrip')) {
    patch.ntrip = mapNtripFromWs(data.ntrip)
  }

  const robotModeSync = buildRobotModeSync(data)
  if (robotModeSync) {
    patch.robotModeSync = robotModeSync
  }

  return patch
}
